import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faSortUp,
  faSortDown,
  faChevronDown,
  faMars,
  faVenus,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";

// Constants
import { useParams } from "react-router-dom";
import { useEventId } from "../EventContext";
import { getFlagCode } from "@vionixasia/shared-components/lib/countryCodes";

const PAGE_SIZE = 50;
const CATEGORIES = [
  { id: "all", displayName: "All Categories" },
  { id: "7K", displayName: "7K Sembalun Run" },
  { id: "21K", displayName: "Half Marathon" },
  { id: "42K", displayName: "Marathon" },
];
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

if (!API_BASE_URL) {
  throw new Error(
    "VITE_API_BASE_URL is not defined. Please set it in the environment variables."
  );
}

// Convert nationality to ISO flag code and display name
// Format Gender with Icons (only for desktop)
const formatGender = (gender) => {
  if (!gender || typeof gender !== "string") return "-";
  const normalizedGender = gender.toLowerCase();
  if (normalizedGender === "male" || normalizedGender === "m")
    return (
      <>
        <FontAwesomeIcon icon={faMars} className="mr-1" /> Male
      </>
    );
  if (normalizedGender === "female" || normalizedGender === "f")
    return (
      <>
        <FontAwesomeIcon icon={faVenus} className="mr-1" /> Female
      </>
    );
  return "-";
};

// SkeletonRow Component for loading state
const SkeletonRow = React.memo(() => (
  <tr className="border-b border-gray-200">
    {[...Array(5)].map((_, index) => (
      <td key={index} className="px-4 py-4">
        <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
      </td>
    ))}
  </tr>
));

// Debounce Hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

// InputField Component
const InputField = ({
  name,
  type,
  value,
  onChange,
  placeholder,
  inputClassName,
  startIcon,
}) => (
  <div className="relative">
    {startIcon && (
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        {startIcon}
      </div>
    )}
    <input
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`border border-gray-300 p-2 pl-4 rounded-xl w-full md:w-64 text-center shadow-sm focus:outline-none focus:ring-1 focus:ring-lime-400 bg-white dark:bg-white text-slate-800 text-sm ${inputClassName}`}
      aria-label={placeholder}
    />
  </div>
);

// CategoryButton Component
const CategoryButton = React.memo(({ category, selectedCategory, onClick }) => (
  <button
    onClick={() => onClick(category.id)}
    className={`px-4 py-2 rounded-xl text-sm font-medium uppercase transition-colors ${
      selectedCategory === category.id
        ? "bg-lime-600 text-white"
        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
    }`}
    aria-label={`Filter participants by category ${category.displayName}`}
  >
    {category.displayName}
  </button>
));

// Error Boundary Component
class ErrorBoundary extends React.Component {
  state = { error: null };
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <section className="min-h-screen flex items-center justify-center flex-col bg-white text-center px-4">
          <p className="text-lg text-red-600 mb-4 font-oxanium">
            Error: {this.state.error.message}
          </p>
          <p className="text-sm text-gray-600">
            Please check the backend connection or server configuration.
          </p>
        </section>
      );
    }
    return this.props.children;
  }
}

export const StartlistSMM = () => {
  const { realEventId: eventId } = useEventId();
  const [state, setState] = useState({
    participants: [],
    allParticipants: [],
    filteredParticipants: [],
    totalByCategory: { "7K": 0, "21K": 0, "42K": 0 },
    totalAll: 0,
    searchTerm: "",
    selectedCategory: "all",
    loading: true,
    loadingMore: false,
    error: null,
    hasMore: true,
    sortGenderDirection: null,
    searchPageIndex: 0,
  });

  const isMounted = useRef(true);
  const debouncedSearchTerm = useDebounce(state.searchTerm, 300);

  // Cleanup on unmount
  useEffect(() => {
    window.scrollTo(0, 0);
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchAllParticipants = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/timing/participants`,
        {
          params: { eventId: eventId },
          timeout: 10000,
        }
      );

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Invalid response: Data is not JSON");
      }

      const participants = response.data.participants || [];
      const totalByCategory = { "7K": 0, "21K": 0, "42K": 0 };

      participants.forEach((participant) => {
        if (CATEGORIES.some((cat) => cat.id === participant.categoryId)) {
          totalByCategory[participant.categoryId] += 1;
        }
      });

      const totalAll = participants.length;
      const formattedParticipants = participants.map((p, index) => ({
        id: `${p.bib}-${index}`,
        name: p.name || "Name not available",
        gender: p.gender || "Unknown",
        categoryId: p.categoryId || "Unknown",
        bib: p.bib || "Unknown",
        nationality: p.nationality || "N/A",
      }));

      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          allParticipants: formattedParticipants,
          participants: formattedParticipants.slice(0, PAGE_SIZE),
          filteredParticipants: formattedParticipants.slice(0, PAGE_SIZE),
          totalByCategory,
          totalAll,
          loading: false,
          hasMore: formattedParticipants.length > PAGE_SIZE,
        }));
      }
    } catch (err) {
      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          error: `Failed to load participants from backend: ${err.message}${
            err.response ? ` (Status: ${err.response.status})` : ""
          }`,
          loading: false,
        }));
      }
    }
  }, []);

  // Load initial data
  useEffect(() => {
    fetchAllParticipants();
  }, [fetchAllParticipants]);

  // Handle category change
  const handleCategoryChange = useCallback(
    (categoryId) => {
      if (categoryId === state.selectedCategory) return;
      setState((prev) => ({
        ...prev,
        selectedCategory: categoryId,
        searchTerm: "",
        searchPageIndex: 0,
        hasMore: true,
      }));
    },
    [state.selectedCategory]
  );

  // Handle search and category filtering
  useEffect(() => {
    let filteredList = state.allParticipants;

    // Filter by category
    if (state.selectedCategory !== "all") {
      filteredList = filteredList.filter(
        (p) => p.categoryId === state.selectedCategory
      );
    }

    // Filter by search term
    if (debouncedSearchTerm) {
      filteredList = filteredList.filter((p) =>
        p.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase().trim())
      );
    }

    setState((prev) => ({
      ...prev,
      filteredParticipants: filteredList.slice(0, PAGE_SIZE),
      totalFiltered: filteredList.length,
      hasMore: filteredList.length > PAGE_SIZE,
      searchPageIndex: 0,
    }));
  }, [debouncedSearchTerm, state.allParticipants, state.selectedCategory]);

  // Load more participants
  const loadMore = useCallback(() => {
    setState((prev) => {
      const currentIndex = prev.searchPageIndex + 1;
      const startIndex = currentIndex * PAGE_SIZE;
      let filteredList = prev.allParticipants;

      // Filter by category
      if (prev.selectedCategory !== "all") {
        filteredList = filteredList.filter(
          (p) => p.categoryId === prev.selectedCategory
        );
      }

      // Filter by search term
      if (debouncedSearchTerm) {
        filteredList = filteredList.filter((p) =>
          p.name
            .toLowerCase()
            .includes(debouncedSearchTerm.toLowerCase().trim())
        );
      }

      const additionalList = filteredList.slice(
        startIndex,
        startIndex + PAGE_SIZE
      );
      if (!additionalList.length) return { ...prev, hasMore: false };

      return {
        ...prev,
        filteredParticipants: [...prev.filteredParticipants, ...additionalList],
        hasMore: startIndex + PAGE_SIZE < filteredList.length,
        searchPageIndex: currentIndex,
      };
    });
  }, [
    debouncedSearchTerm,
    state.allParticipants,
    state.searchPageIndex,
    state.selectedCategory,
  ]);

  // Handle gender sort toggle
  const handleGenderSortToggle = useCallback(() => {
    const newDirection = state.sortGenderDirection === "asc" ? "desc" : "asc";
    setState((prev) => {
      const sortedParticipants = [...prev.allParticipants].sort((a, b) => {
        if (newDirection === "asc") {
          return a.gender.localeCompare(b.gender);
        }
        return b.gender.localeCompare(a.gender); // Fixed: was b.gender.localeCompare(b.gender)
      });
      let filteredList = sortedParticipants;

      if (prev.selectedCategory !== "all") {
        filteredList = filteredList.filter(
          (p) => p.categoryId === prev.selectedCategory
        );
      }

      if (debouncedSearchTerm) {
        filteredList = filteredList.filter((p) =>
          p.name
            .toLowerCase()
            .includes(debouncedSearchTerm.toLowerCase().trim())
        );
      }

      return {
        ...prev,
        sortGenderDirection: newDirection,
        allParticipants: sortedParticipants,
        participants: sortedParticipants.slice(0, PAGE_SIZE),
        filteredParticipants: filteredList.slice(0, PAGE_SIZE),
        totalFiltered: filteredList.length,
        hasMore: filteredList.length > PAGE_SIZE,
        searchPageIndex: 0,
      };
    });
  }, [state.sortGenderDirection, state.selectedCategory, debouncedSearchTerm]);

  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    const searchValue = e.target.value || "";
    setState((prev) => ({
      ...prev,
      searchTerm: searchValue,
      searchPageIndex: 0,
    }));
  }, []);

  const rangeText = `${state.filteredParticipants.length} of ${
    debouncedSearchTerm || state.selectedCategory !== "all"
      ? state.totalFiltered
      : state.totalAll
  } Participants`;

  if (state.error) {
    return (
      <section className="min-h-screen flex items-center justify-center flex-col bg-white text-center px-4">
        <p className="text-lg text-red-600 mb-4 font-oxanium">{state.error}</p>
      </section>
    );
  }

  return (
    <ErrorBoundary>
      <section className="min-h-screen mt-10 bg-white mb-10">
        <div className="relative z-1 container mx-auto px-4">
          <motion.div
            className="mb-6 flex flex-col gap-4 max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg md:text-xl font-bold uppercase text-lime-600 text-center block md:hidden">
              SM MARATHON 2025 STARTLIST
            </h3>
            <div className="w-full max-w-md mx-auto md:ml-0">
              <InputField
                name="search"
                type="text"
                value={state.searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by name..."
                inputClassName=""
                startIcon={
                  <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
                }
              />
            </div>
            <div className="flex flex-col md:flex-row-reverse md:items-center md:justify-between gap-4">
              <h3 className="text-lg md:text-xl font-bold uppercase text-lime-600 md:text-right hidden md:block">
                SM MARATHON 2025 STARTLIST
              </h3>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((category) => (
                  <CategoryButton
                    key={category.id}
                    category={category}
                    selectedCategory={state.selectedCategory}
                    onClick={handleCategoryChange}
                  />
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl border border-gray-300 overflow-x-auto max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            viewport={{ once: true }}
          >
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 table-auto">
                <thead className="bg-gray-50">
                  <tr>
                    {/* Name */}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[150px]">
                      Name
                    </th>
                    {/* Bib */}
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bib
                    </th>
                    {/* Nationality - hidden on mobile */}
                    <th className="hidden md:table-cell px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nationality
                    </th>
                    {/* Gender - hidden on mobile */}
                    <th
                      className="hidden md:table-cell px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[150px] cursor-pointer hover:text-lime-600"
                      onClick={handleGenderSortToggle}
                      aria-label="Sort by gender"
                    >
                      <span className="flex items-center justify-center">
                        Gender
                        {state.sortGenderDirection && (
                          <FontAwesomeIcon
                            icon={
                              state.sortGenderDirection === "asc"
                                ? faSortUp
                                : faSortDown
                            }
                            className="ml-1 text-gray-500"
                          />
                        )}
                      </span>
                    </th>
                    {/* Category - hidden on mobile */}
                    <th className="hidden md:table-cell px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {state.loading ? (
                    [...Array(5)].map((_, index) => <SkeletonRow key={index} />)
                  ) : state.filteredParticipants.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-4 text-center text-sm text-gray-600"
                      >
                        {state.searchTerm
                          ? "No participants match the search criteria."
                          : state.selectedCategory !== "all"
                          ? "No participants registered for this category."
                          : "No participants registered."}
                      </td>
                    </tr>
                  ) : (
                    state.filteredParticipants.map((participant) => {
                      const { code: flagCode, display: nationalityDisplay } =
                        getFlagCode(participant.nationality);
                      return (
                        <tr
                          key={participant.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          {/* Name - always visible */}
                          <td
                            className={`px-4 py-4 text-sm text-gray-600 uppercase max-w-[150px] truncate ${
                              participant.gender === "female"
                                ? "text-lime-600"
                                : "text-gray-600"
                            }`}
                          >
                            {participant.name.toUpperCase()}
                          </td>
                          {/* Bib - always visible */}
                          <td className="px-4 py-4 text-sm text-gray-600 text-center">
                            <span className="border border-gray-300 rounded-full px-2 py-1">
                              {participant.bib ? `BIB ${participant.bib}` : "-"}
                            </span>
                          </td>
                          {/* Nationality - desktop only */}
                          <td className="hidden md:table-cell px-4 py-4 text-sm text-gray-600 text-center">
                            <span className="border border-gray-300 rounded-full px-2 py-1 inline-flex items-center">
                              <img
                                src={`https://flagcdn.com/w20/${flagCode}.png`}
                                onError={(e) => {
                                  e.target.src =
                                    "https://flagcdn.com/w20/un.png";
                                }}
                                className="country-flag w-4 h-3 mr-1 border border-gray-300 rounded"
                                alt={nationalityDisplay}
                              />
                              {nationalityDisplay || "-"}
                            </span>
                          </td>
                          {/* Gender - desktop only */}
                          <td className="hidden md:table-cell px-4 py-4 text-sm text-gray-600 text-center max-w-[150px]">
                            <span className="border border-gray-300 rounded-full px-2 py-1">
                              {formatGender(participant.gender)}
                            </span>
                          </td>
                          {/* Category - desktop only */}
                          <td className="hidden md:table-cell px-4 py-4 text-sm text-gray-600 text-center">
                            {participant.categoryId === "7K"
                              ? "7K Sembalun Run"
                              : participant.categoryId === "21K"
                              ? "Half Marathon"
                              : "Marathon"}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Load More */}
            <div className="flex justify-end items-center px-4 py-2">
              <span className="text-sm text-gray-600 mr-4">{rangeText}</span>
              <button
                onClick={loadMore}
                disabled={state.loading || !state.hasMore}
                className={`px-3 py-1 rounded-lg transition-colors duration-200 flex items-center gap-1
                  ${
                    state.loading || !state.hasMore
                      ? "bg-gray-200 text-gray-600 cursor-not-allowed"
                      : "bg-lime-600 text-white hover:bg-lime-700"
                  }`}
                aria-label={
                  state.hasMore
                    ? "Load more participants"
                    : "End of participant list"
                }
              >
                <span>
                  {state.hasMore ? "Load More" : "End of Participants"}
                </span>
                <FontAwesomeIcon icon={faChevronDown} />
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </ErrorBoundary>
  );
};

export default StartlistSMM;
